/**
 * The frozen purchase order document as the server reports it (batch 8 T12c1, #3033): the recipient, the order, the lines, the
 * totals and what would be sent. Everything is shown as TEXT. The formatted (HTML) version of an email is never rendered as markup:
 * it carries the same data as the text version, and showing a supplier-facing document through `dangerouslySetInnerHTML` would make
 * every stored value a possible script.
 */

import {
  Box,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { DispatchSnapshot, RenderedApiRequest, RenderedEmail } from './dispatchTypes';
import { formatCalendarDate, formatCents4, formatPercent4 } from './dispatchFormat';

interface DocumentViewProps {
  snapshot: DispatchSnapshot;
  rendered?: { email: RenderedEmail } | { api: RenderedApiRequest } | undefined;
  /** The email address the document went to / would go to, when the snapshot has none (a history answer carries it separately). */
  recipientEmail?: string | null | undefined;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
        {children}
      </Typography>
    </>
  );
}

export function DispatchDocumentView({ snapshot, rendered, recipientEmail }: DocumentViewProps) {
  const { t, i18n } = useTranslation('procurementDispatch');
  const language = i18n.language;
  const currency = snapshot.order.currency;
  const recipient = snapshot.recipient?.email ?? recipientEmail ?? null;
  const email = rendered && 'email' in rendered ? rendered.email : null;

  return (
    <Stack spacing={2}>
      <Box
        component="dl"
        sx={{
          m: 0,
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: 2,
          rowGap: 0.5,
        }}
      >
        <Fact label={t('document.channel')}>{t(`channel.${snapshot.channel}`)}</Fact>
        {snapshot.channel === 'email' && (
          <Fact label={t('document.recipient')}>{recipient ?? '—'}</Fact>
        )}
        <Fact label={t('document.language')}>{t(`language.${snapshot.language}`)}</Fact>
        <Fact
          label={t('document.supplier')}
        >{`${snapshot.supplier.name} (${snapshot.supplier.code})`}</Fact>
        <Fact label={t('document.orderNumber')}>{snapshot.order.orderNumber}</Fact>
        <Fact label={t('document.orderDate')}>
          {formatCalendarDate(snapshot.order.orderDate, language)}
        </Fact>
        <Fact label={t('document.expectedDelivery')}>
          {formatCalendarDate(snapshot.order.expectedDeliveryDate, language)}
        </Fact>
        {snapshot.order.notes ? (
          <Fact label={t('document.notes')}>{snapshot.order.notes}</Fact>
        ) : null}
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label={t('document.linesTable')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('document.line')}</TableCell>
              <TableCell>{t('document.partNumber')}</TableCell>
              <TableCell>{t('document.description')}</TableCell>
              <TableCell align="right">{t('document.quantity')}</TableCell>
              <TableCell align="right">{t('document.unitPrice')}</TableCell>
              <TableCell align="right">{t('document.discount')}</TableCell>
              <TableCell align="right">{t('document.lineTotal')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {snapshot.lines.map((line) => (
              <TableRow key={line.lineId}>
                <TableCell>{line.lineNumber}</TableCell>
                <TableCell>{line.partNumber}</TableCell>
                <TableCell>
                  {line.description}
                  {line.notes ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {line.notes}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right">{`${line.quantity} ${line.unit}`}</TableCell>
                <TableCell align="right">
                  {formatCents4(line.unitPriceNetCents, language, currency)}
                </TableCell>
                <TableCell align="right">
                  {formatPercent4(line.discountPercent, language)}
                </TableCell>
                <TableCell align="right">
                  {formatCents4(line.lineNetCents, language, currency)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        component="dl"
        sx={{
          m: 0,
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: 2,
          rowGap: 0.5,
          justifyContent: 'end',
        }}
      >
        <Fact label={t('document.linesTotal')}>
          {formatCents4(snapshot.totals.linesNetCents, language, currency)}
        </Fact>
        <Fact label={t('document.shipping')}>
          {formatCents4(snapshot.totals.shippingNetCents, language, currency)}
        </Fact>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {t('document.netNote')}
      </Typography>

      {email ? (
        <>
          <Divider />
          <Typography variant="subtitle2">{t('document.emailContent')}</Typography>
          <Box
            component="dl"
            sx={{
              m: 0,
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              columnGap: 2,
              rowGap: 0.5,
            }}
          >
            <Fact label={t('document.to')}>{email.to}</Fact>
            <Fact label={t('document.subject')}>{email.subject}</Fact>
          </Box>
          <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 280, overflow: 'auto' }}>
            <Typography
              component="pre"
              variant="body2"
              sx={{ m: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontFamily: 'inherit' }}
            >
              {email.text}
            </Typography>
          </Paper>
        </>
      ) : null}
      {rendered && 'api' in rendered ? (
        <>
          <Divider />
          <Typography variant="body2">
            {t('document.apiContent', { lines: snapshot.lines.length })}
          </Typography>
        </>
      ) : null}
    </Stack>
  );
}
